import { z } from 'zod';
import { getApiInstance, harden, Response, Cache } from '../core';

export const forecastWeatherValidator = z.object({
    condition: z.string(),
    date: z.date(),
    icon: z.string(),
    temp_high: z.number(),
    temp_low: z.number(),
    timestamp: z.number(),
    wind_direction: z.number(),
    wind_speed: z.number(),
    feels_like: z.number(),
});

export const currentWeatherValidator = z.object({
    condition: z.string(),
    date: z.date(),
    icon: z.string(),
    temperature: z.number(),
    timestamp: z.number(),
    wind_direction: z.number(),
    wind_speed: z.number(),
    feels_like: z.number(),
});

export const getWeatherDataValidator = z.object({
    status: z.literal('OK'),
    message: z.object({
        code: z.literal('NO_ERROR'),
    }),
    version: z.literal('v3'),
    last_accessed: z.string(),
    last_updated: z.string(),
    next_update: z.string(),
    weather: z.object({
        current: z.array(
            z
                .object({
                    weather_locality_id: z.string(),
                    locality: z.string(),
                    area: z.string().nullable(),
                    description: z.string(),
                    current_generation: z.literal(-1),
                })
                .and(
                    currentWeatherValidator.extend({
                        date: z.string(),
                    })
                )
        ),
        forecast: z.array(
            z
                .object({
                    weather_locality_id: z.string(),
                    current_generation: z.literal(-1),
                })
                .and(
                    forecastWeatherValidator.extend({
                        date: z.string(),
                    })
                )
        ),
        hourly: z.array(
            z
                .object({
                    weather_locality_id: z.string(),
                    current_generation: z.literal(-1),
                })
                .and(
                    forecastWeatherValidator.extend({
                        date: z.string(),
                    })
                )
        ),
    }),
});

export async function getWeatherLocation(
    locationId: string
): Response<z.infer<typeof getWeatherDataValidator>> {
    const { api, cacheConfig } = await getApiInstance();
    const urlPath = `weather/location/${encodeURIComponent(locationId)}`;

    cacheConfig(Cache.GetWithFallback, urlPath);

    const response = await api.get(urlPath);

    return harden(getWeatherDataValidator, response.data);
}
